import {isUndefined} from 'lodash';
import React, {FC, SyntheticEvent, useEffect, useRef, useState} from 'react';

import {VisuallyHidden} from '../../../../../libs/shared/ui/src/components/visuallyHidden/visuallyHidden';
import {getFileContentType} from '../../../../helpers/fileHelpers';
import {ContentEditableHtmlEditor} from '../../../common/editor/html/contentEditableHtmlEditor';
import {ServiceSubscription} from '../../../common/editor/serviceSubscription';
import {useApplyFormattingToEditor} from './applyFormatting';
import {FormattingImage} from './formattingImage';
import {FormattingImageDialog} from './formattingImageDialog';

/*
 * Props.
 */

interface FormattingImageContainerProps {
  htmlEditor: ContentEditableHtmlEditor;
  onImageUpload: (file: File) => void;
}

/*
 * Component.
 */

export const FormattingImageContainer: FC<FormattingImageContainerProps> = (props) => {
  const {onImageUpload} = props;
  const [fileUploadInputKey, setFileUploadInputKey] = useState(0);
  const [isImageDialogOpen, setIsImageDialogOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [htmlEditor, setHtmlEditor] = useState<ContentEditableHtmlEditor>(props.htmlEditor);
  const applyFormatting = useApplyFormattingToEditor(htmlEditor, false);

  useEffect(() => {
    if (isImageDialogOpen) {
      return;
    }

    setHtmlEditor(props.htmlEditor);
  }, [isImageDialogOpen, props.htmlEditor]);

  const onFileInputChange = (event: SyntheticEvent<HTMLInputElement>) => {
    const {files} = event.currentTarget;
    const fileList = files && Array.from(files);
    if (fileList && fileList.length > 0) {
      fileList.forEach(
        // Only upload the images
        (file) => getFileContentType(file).startsWith('image/') && onImageUpload(file),
      );
    }

    // Re-create the input to avoid existing-value issues.
    setFileUploadInputKey((f) => f + 1);
  };
  const onUploadImageInlineClick = () => {
    if (!fileInputRef.current) {
      return;
    }

    fileInputRef.current.click();
  };

  const onImageUrlChange = (newUrl: string | undefined) => {
    applyFormatting((editor) => editor.services.image.setUrl(newUrl), false);
  };

  function maybeRenderImageDialog() {
    if (!isImageDialogOpen) {
      return null;
    }

    const {url} = htmlEditor.services.image;

    return (
      <FormattingImageDialog
        url={url}
        onUrlChange={onImageUrlChange}
        onRequestClose={() => setIsImageDialogOpen(false)}
      />
    );
  }

  return (
    <>
      <ServiceSubscription source={htmlEditor.services}>
        {(services) => (
          <FormattingImage
            isImageSelectedInComposer={!isUndefined(services.image.url)}
            onInsertImageUrlInlineClick={() => setIsImageDialogOpen(true)}
            onUploadImageInlineClick={onUploadImageInlineClick}
          />
        )}
      </ServiceSubscription>
      {maybeRenderImageDialog()}
      <VisuallyHidden>
        <input
          ref={fileInputRef}
          key={fileUploadInputKey}
          type="file"
          accept="image/*"
          onChange={onFileInputChange}
          multiple
        />
      </VisuallyHidden>
    </>
  );
};
